import { Component, OnInit, Input, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { Route } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Interface para los submódulos del grid
 */
export interface SubmoduleCard {
  path: string;
  title: string;
  description: string;
  icon: string; // Iconify icon name
  theme: string;
  bgGradient?: string;
}

/**
 * Interface para metadata de rutas
 */
export interface SubmoduleRouteData {
  title?: string;
  description?: string;
  icon?: string;
  theme?: string;
  bgGradient?: string;
}

/**
 * Componente reutilizable de grid de submódulos
 * 
 * Lee dinámicamente las rutas hijas y sus metadatos desde route.data
 * Muestra tarjetas con iconos iconify y efectos visuales similares al carousel
 * 
 * @example
 * ```html
 * <app-submodules-grid 
 *   [childRoutes]="routes" 
 *   [title]="'Módulos del Sistema'"
 *   [subtitle]="'Seleccione un módulo'">
 * </app-submodules-grid>
 * ```
 */
@Component({
  selector: 'app-submodules-grid',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  templateUrl: './submodules-grid.component.html',
  styleUrls: ['./submodules-grid.component.scss']
})
export class SubmodulesGridComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  @Input() childRoutes: Route[] = [];
  @Input() title: string = 'Módulos Disponibles';
  @Input() subtitle: string = 'Seleccione un módulo para gestionar su configuración';
  @Input() basePath?: string; // Ruta base para navegación (opcional, se detecta automáticamente)
  /** Carrusel con máscara y marquee (p. ej. Configuración global) */
  @Input() carouselMode = false;

  submodules = signal<SubmoduleCard[]>([]);
  readonly isCarouselPaused = signal(false);

  readonly infiniteSubmodules = computed(() => {
    const list = this.submodules();
    if (!this.carouselMode || list.length === 0) {
      return list;
    }
    return [...list, ...list, ...list];
  });

  /** Iconos por defecto rotativos si la ruta no define `data.icon` (evita repetir el mismo widget) */
  private readonly defaultIconsByIndex = [
    'solar:buildings-2-bold-duotone',
    'solar:diagram-up-bold-duotone',
    'solar:case-round-bold-duotone',
    'solar:settings-bold-duotone',
    'solar:document-text-bold-duotone',
    'solar:chart-square-bold-duotone',
    'solar:users-group-rounded-bold-duotone',
    'solar:shield-check-bold-duotone',
  ];

  // Temas disponibles para asignar automáticamente cuando no se especifica en route.data
  private availableThemes = [
    { theme: 'blue', bgGradient: 'from-blue-500 to-cyan-500' },
    { theme: 'emerald', bgGradient: 'from-emerald-500 to-teal-500' },
    { theme: 'purple', bgGradient: 'from-purple-500 to-pink-500' },
    { theme: 'orange', bgGradient: 'from-orange-500 to-amber-500' },
    { theme: 'pink', bgGradient: 'from-pink-500 to-rose-500' },
    { theme: 'cyan', bgGradient: 'from-cyan-500 to-blue-500' },
    { theme: 'indigo', bgGradient: 'from-indigo-500 to-purple-500' },
    { theme: 'green', bgGradient: 'from-green-500 to-emerald-500' },
    { theme: 'teal', bgGradient: 'from-teal-500 to-cyan-500' },
    { theme: 'amber', bgGradient: 'from-amber-500 to-orange-500' },
  ];

  ngOnInit(): void {
    this.loadSubmodules();
  }

  /**
   * Carga los submódulos dinámicamente desde las rutas hijas
   * Lee la metadata desde route.data
   */
  private loadSubmodules(): void {
    if (this.childRoutes && this.childRoutes.length > 0) {
      const submoduleCards: SubmoduleCard[] = this.childRoutes
        .filter(
          (child) =>
            child.path &&
            child.path !== '' &&
            !String(child.path).includes(':'),
        )
        .map((child, index) => {
          const path = child.path!;
          const routeData = (child.data || {}) as SubmoduleRouteData;
          
          // Obtener metadata desde route.data o usar valores por defecto
          const metadata = this.getMetadataFromRoute(routeData, path, index);
          
          return {
            path: path,
            ...metadata
          };
        });

      this.submodules.set(submoduleCards);
    }
  }

  /**
   * Obtiene metadata desde route.data o genera valores por defecto
   */
  private getMetadataFromRoute(
    routeData: SubmoduleRouteData, 
    path: string, 
    index: number
  ): Omit<SubmoduleCard, 'path'> {
    const themeConfig = routeData.theme 
      ? this.availableThemes.find(t => t.theme === routeData.theme) || this.availableThemes[index % this.availableThemes.length]
      : this.availableThemes[index % this.availableThemes.length];
    
    const title = routeData.title || this.capitalizeFirst(path);
    const description = routeData.description || `Gestión de ${title.toLowerCase()}`;
    const icon =
      routeData.icon || this.defaultIconsByIndex[index % this.defaultIconsByIndex.length];
    const theme = routeData.theme || themeConfig.theme;
    const bgGradient = routeData.bgGradient || themeConfig.bgGradient;
    
    return {
      title,
      description,
      icon,
      theme,
      bgGradient
    };
  }

  /**
   * Capitaliza la primera letra de una cadena
   */
  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Navega a un submódulo
   * Usa basePath si está definido, sino detecta automáticamente desde la URL actual
   */
  navigateToSubmodule(path: string): void {
    if (this.basePath) {
      this.router.navigate([this.basePath, path]);
    } else {
      const currentUrl = this.router.url;
      // Si la URL termina con una ruta específica, navegar relativo a ella
      if (currentUrl && !currentUrl.endsWith('/')) {
        this.router.navigate([currentUrl, path]);
      } else {
        // Construir la ruta base desde la URL actual
        const urlParts = currentUrl.split('/').filter(p => p);
        this.router.navigate([...urlParts, path]);
      }
    }
  }

  /**
   * TrackBy function para optimizar el renderizado
   */
  trackByPath(index: number, card: SubmoduleCard): string {
    return card.path;
  }

  trackByCarousel(index: number, card: SubmoduleCard): string {
    return `${card.path}-${index}`;
  }

  onCarouselMouseEnter(): void {
    if (this.carouselMode) {
      this.isCarouselPaused.set(true);
    }
  }

  onCarouselMouseLeave(): void {
    this.isCarouselPaused.set(false);
  }
}

